import { Component, Input, ViewChild } from '@angular/core';
import { AbstractComponent } from '../abstract-component';
import {
  faCircleExclamation,
  faHourglassHalf,
  faSquareCheck,
  faTriangleExclamation,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { faAddressBook, faCopy } from '@fortawesome/free-regular-svg-icons';
import { SVGIcon, undoIcon, xIcon } from '@progress/kendo-svg-icons';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { Observable, takeUntil, timer } from 'rxjs';
import { Clipboard } from '@angular/cdk/clipboard';
import { FormattedDates } from 'src/app/modules/dashboard/interface/formatted-dates.';
import { CompleteActionItemDialogService } from '../../services/complete-action-item-dialog.service';
import { SendBackIhmsDialogService } from '../../services/send-back-ihms-dialog.service';
import { IHMSStudentResponse } from '../../interfaces/ihms-dashboard-response';
import { IhmsGridStatus } from '../../enums/ihms-grid-status';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  DialogCloseResult,
  DialogResult,
} from '@progress/kendo-angular-dialog';
import { SendBackIhmsComponent } from '../send-back-ihms/send-back-ihms.component';
import { CompleteActionItemComponent } from '../complete-action-item/complete-action-item.component';
import { IhmsService } from '../../services/ihms.service';
import { AssignActionItemsDialogService } from '../../services/assign-action-item-dialog.service';
import { AssignActionItemDialogComponent } from '../assign-action-item-dialog/assign-action-item-dialog.component';

@Component({
  selector: 'app-ihms-detail-action-items-grid',
  templateUrl: './ihms-detail-action-items-grid.component.html',
  styleUrls: ['./ihms-detail-action-items-grid.component.scss'],
  standalone: false,
})
export class IhmsDetailActionItemsGridComponent extends AbstractComponent {
  @Input() actionItems: IHMSStudentResponse[] = [];
  @ViewChild(TooltipDirective) public tooltipDir: TooltipDirective;
  squareCheck: SVGIcon = this.customFaIcon(faSquareCheck as IconDefinition);
  undoIcon: SVGIcon = undoIcon;
  addressBook: SVGIcon = this.customFaIcon(faAddressBook as IconDefinition);
  copy: SVGIcon = this.customFaIcon(faCopy as IconDefinition);
  xIcon: SVGIcon = xIcon;
  tooltipText: string = 'Copy';
  dueDatesArray: FormattedDates[] = [];
  readonly tooltipResetDelayMS: number = 3000;
  readonly singleDay: number = 1;
  ihmsStatus: typeof IhmsGridStatus = IhmsGridStatus;
  @Input() studentId: string;
  @Input() studentName: string;
  constructor(
    private clipboard: Clipboard,
    private completeActionItemDialogService: CompleteActionItemDialogService,
    private sendBackIhmsDialogService: SendBackIhmsDialogService,
    library: FaIconLibrary,
    private ihmsService: IhmsService,
    private assignActionItemsDialogService: AssignActionItemsDialogService
  ) {
    super();
    library.addIcons(
      faTriangleExclamation,
      faCircleExclamation,
      faHourglassHalf
    );
  }
  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      content: `<path d="${icon.icon[4]}" />`,
      viewBox: `0 0 ${icon.icon[0]} ${icon.icon[1]}`,
    };
  }
  copyClipboard(copiedText: string): void {
    this.clipboard.copy(copiedText);
    this.tooltipText = 'Copied';
    timer(this.tooltipResetDelayMS)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(() => {
        this.tooltipText = 'Copy';
        this.tooltipDir?.hide();
      });
  }

  afterOnDestroy(): void {
    // Cleanup if needed
  }

  getClasses(status: number): string {
    if (status === IhmsGridStatus.Assigned) {
      return 'assigned';
    } else if (status === IhmsGridStatus.New) {
      return ' new';
    } else if (status === IhmsGridStatus.Returned) {
      return ' returned';
    } else if (status === IhmsGridStatus.Completed) {
      return ' completed';
    } else if (status === IhmsGridStatus.SubmittedForReview) {
      return 'Submitted For Review';
    }
    return 'status-badge';
  }

  getStatus(status: number): string {
    if (status === IhmsGridStatus.Assigned) {
      return 'Assigned';
    } else if (status === IhmsGridStatus.New) {
      return ' New';
    } else if (status === IhmsGridStatus.Returned) {
      return ' Returned';
    } else if (status === IhmsGridStatus.Completed) {
      return 'Completed';
    } else if (status === IhmsGridStatus.SubmittedForReview) {
      return 'Submitted For Review';
    }
    return '';
  }
  public rowCallback: (context: RowClassArgs) => Record<string, boolean> = (
    context: RowClassArgs
  ) => {
    if (!context.dataItem.assigneeName) {
      // If there is no assigneeName, apply a red background to the entire row
      return { error: true };
    } else {
      return null;
    }
  };

  public sendBackToIHMS(actionData: IHMSStudentResponse): void {
    const dialogRef: {
      result$: Observable<DialogCloseResult | DialogResult>;
      instance: SendBackIhmsComponent;
    } = this.sendBackIhmsDialogService.open();
    dialogRef.instance.actionDetails = actionData;
    dialogRef.instance.studentId = this.studentId;
    dialogRef.instance.studentName = this.studentName;
  }

  public completeActionItem(actionData: IHMSStudentResponse): void {
    const dialogRef: {
      result$: Observable<DialogCloseResult | DialogResult>;
      instance: CompleteActionItemComponent;
    } = this.completeActionItemDialogService.open();
    dialogRef.instance.actionDetails = actionData;
    dialogRef.instance.studentId = this.studentId;
    dialogRef.result$.pipe(takeUntil(this.componentDestroyed)).subscribe(() => {
      // Handle the result here
    });
  }

  displayAssignActionItemDialog(
    actionData: IHMSStudentResponse,
    reAssign?: boolean
  ): void {
    const dialogRef: {
      result$: Observable<DialogCloseResult | DialogResult>;
      instance: AssignActionItemDialogComponent;
    } = this.assignActionItemsDialogService.open();
    dialogRef.instance.actionDetails = actionData;
    dialogRef.instance.studentId = this.studentId;
    dialogRef.instance.reassign = reAssign;
    dialogRef.instance.studentName = this.studentName;
  }
}
